import React from "react";
import { Link } from "react-router-dom";
import { Download, MapPin } from "lucide-react";
import CTABanner from "../components/CTABanner";
import { SUB_PROCESSORS, GOV_COMPANY, HOSTING_REGION } from "../data/governance";

const DPA_PDF = `${process.env.REACT_APP_BACKEND_URL}/api/public/governance/dpa-summary.pdf`;

const SubProcessors = () => {
  return (
    <>
      <section className="ek-gradient-dark text-white relative overflow-hidden">
        <div className="ek-star-watermark" />
        <div className="ek-container relative z-10 py-20 md:py-28">
          <div className="ek-eyebrow text-[#A179FA]">Governance</div>
          <h1 className="ek-heading text-4xl md:text-5xl lg:text-6xl text-white mt-4 max-w-4xl leading-tight" data-testid="sub-processors-headline">
            Sub-processors &amp; DPA Summary
          </h1>
          <p className="text-zinc-200 font-light text-lg mt-6 max-w-3xl">
            The third-party providers {GOV_COMPANY.legalName} engages to deliver this service, what data each processes, and where. Procurement teams can download a DPA summary for due diligence.
          </p>
          <a
            href={DPA_PDF}
            target="_blank"
            rel="noopener noreferrer"
            className="ek-btn ek-btn-primary mt-8 inline-flex items-center gap-2"
            data-testid="sub-processors-download-dpa"
          >
            <Download size={16} /> Download DPA summary (PDF)
          </a>
        </div>
      </section>

      <section className="bg-white py-16 md:py-20">
        <div className="ek-container max-w-5xl">
          <div className="ek-body text-[16.5px] space-y-4">
            <p>
              {GOV_COMPANY.legalName} acts as a <strong>data controller</strong> for its own marketing and enquiry data, and as a <strong>data processor</strong> for client personal data processed during an engagement. The sub-processors below may process personal data in delivering the service. International transfers are protected by appropriate safeguards (UK IDTA / EU Standard Contractual Clauses) where applicable.
            </p>
          </div>

          <div className="mt-8 flex items-start gap-3 p-4 border-l-2 border-[#7737F6] bg-[#7737F6]/5" data-testid="data-residency-note">
            <MapPin size={18} className="text-[#7737F6] mt-0.5 shrink-0" />
            <p className="ek-body text-[15px]">
              <strong>Primary data residency:</strong> {HOSTING_REGION}. This is where the application and database are hosted. {GOV_COMPANY.legalName} is a UK company subject to UK GDPR; a UK-based data region is planned following the current pilot.
            </p>
          </div>

          <div className="mt-10 overflow-x-auto border border-zinc-200" data-testid="sub-processors-table">
            <table className="min-w-full text-[14.5px]">
              <thead className="bg-zinc-50 text-left text-zinc-500 text-[11px] uppercase tracking-[0.12em]">
                <tr>
                  <th className="p-3">Sub-processor</th>
                  <th className="p-3">Purpose</th>
                  <th className="p-3">Data categories</th>
                  <th className="p-3">Region</th>
                </tr>
              </thead>
              <tbody>
                {SUB_PROCESSORS.map((p) => (
                  <tr key={p.name} className="border-t border-zinc-100 align-top" data-testid={`sub-processor-row-${p.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                    <td className="p-3 font-medium text-zinc-900 whitespace-nowrap">{p.name}</td>
                    <td className="p-3 text-zinc-700">{p.purpose}</td>
                    <td className="p-3 text-zinc-600">{p.data}</td>
                    <td className="p-3 text-zinc-600 whitespace-nowrap">{p.region}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mt-10 p-5 border border-zinc-200 bg-zinc-50">
            <p className="ek-body text-[15px]">
              See also our <Link to="/data-retention" className="text-[#7737F6] hover:underline font-medium" data-testid="sub-processors-retention-link">data-retention schedule</Link> and <Link to="/privacy" className="text-[#7737F6] hover:underline font-medium">privacy policy</Link>. For a signed Data Processing Agreement, contact <a href={`mailto:${GOV_COMPANY.contactEmail}`} className="text-[#7737F6] hover:underline">{GOV_COMPANY.contactEmail}</a>.
            </p>
          </div>
          <p className="text-zinc-500 text-sm mt-8">Last reviewed June 2026. Items marked [TO CONFIRM] are pending verification. The signed DPA governs in the event of conflict.</p>
        </div>
      </section>

      <CTABanner />
    </>
  );
};

export default SubProcessors;
